import { useMemo, useState } from 'react'
import { IntegrationServiceId } from '../../../../../../../common/integrations'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { Button } from '../../../../../../../components/Button/Button'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { useGetDescribeQuery } from '../../../../../../../store/integrations/integrations'
import { FormFieldDefinition } from '../../../hooks'
import './FieldReferenceFieldsPicker.scss'

interface FieldReferenceFieldsPickerProps {
  serviceId: IntegrationServiceId
  referenceObjectName: string
  referenceFields: string[]
  onChange: (referenceFields: string[]) => void
  maxFields?: number
  alwaysExpanded?: boolean
  title?: string
  description?: React.ReactNode
}

export const FieldReferenceFieldsPicker: React.FC<FieldReferenceFieldsPickerProps> = ({
  serviceId,
  referenceObjectName,
  referenceFields,
  onChange,
  maxFields,
  alwaysExpanded = false,
  title,
  description,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(alwaysExpanded)

  const accountId = useAppSelector(selectAccountId)
  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    { accountId, connector: serviceId, context: { objectName: referenceObjectName } },
    { skip: !isExpanded || !accountId },
  )
  const formFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])
  const fieldOptions = useMemo((): SelectOption[] => formFieldDefinitions.map((f) => ({ id: f.name, label: `${f.label} (${f.name})` })), [formFieldDefinitions])

  const [extraRows, setExtraRows] = useState<number>(referenceFields.length === 0 ? 1 : 0)
  const rowCount = referenceFields.length + extraRows
  const maxRowCount = Math.min(maxFields ?? fieldOptions.length, fieldOptions.length)
  const canAddMore = rowCount < maxRowCount

  const handleSelect = (index: number, option: SelectOption) => {
    const wasEmpty = !referenceFields[index]
    const updated = [...referenceFields]
    updated[index] = option.id
    onChange(updated)
    if (wasEmpty) {
      setExtraRows((prev) => Math.max(0, prev - 1))
    }
  }

  const handleRemove = (index: number) => {
    onChange(referenceFields.filter((_, i) => i !== index))
  }

  const handleAddField = () => setExtraRows((prev) => prev + 1)
  const handleRemoveEmptyRow = () => setExtraRows((prev) => Math.max(0, prev - 1))

  if (!isExpanded) {
    return (
      <Button type='button' variant='primaryLinkButton' className='p-0 text-decoration-none small' onClick={() => setIsExpanded(true)}>
        Configure reference fields ({referenceFields.length})
      </Button>
    )
  }

  return (
    <div className='mb-3'>
      {title && <h4>{title}</h4>}
      {description && <p className='zocks-text-gray-600 mb-3'>{description}</p>}
      {!alwaysExpanded && (
        <Button type='button' variant='primaryLinkButton' className='p-0 text-decoration-none small mb-2' onClick={() => setIsExpanded(false)}>
          Configure reference fields ({referenceFields.length})
        </Button>
      )}
      <div className={alwaysExpanded ? '' : 'reference-fields-indent-wrapper'}>
        <div className='d-flex flex-column gap-2 mb-2'>
          {Array.from({ length: rowCount }, (_, i) => i).map((i) => {
            const currentId = referenceFields[i]
            const selected: SelectOption | null = currentId ? (fieldOptions.find((o) => o.id === currentId) ?? { id: currentId, label: currentId }) : null
            const availableOptions = fieldOptions.filter((o) => !referenceFields.includes(o.id) || o.id === currentId)
            return (
              <div key={i} className={`reference-fields-row ${alwaysExpanded ? 'reference-fields-row--wide' : 'reference-fields-row--nested'}`}>
                <div className='reference-fields-row__cell'>
                  <div className='reference-fields-row__dropdown'>
                    <SelectDropdown
                      options={availableOptions}
                      initialSelectedOption={selected}
                      onOptionSelected={(option) => handleSelect(i, option)}
                      placeholder='Select field'
                      searchable
                      loading={isDescribeFetching}
                    />
                  </div>
                  <Button
                    type='button'
                    variant='primaryGhost'
                    className='p-2'
                    onClick={() => (currentId ? handleRemove(i) : handleRemoveEmptyRow())}
                    title='Remove reference field'
                  >
                    <i className='bi bi-trash' />
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
        {canAddMore && (
          <Button type='button' variant='primaryGhost' className='border-0' onClick={handleAddField}>
            + Add field
          </Button>
        )}
      </div>
    </div>
  )
}
